import "./MissionsFilters.scss";
import InfiniteScroll from "react-infinite-scroller";
import Loader from "core/loader/LoaderV2";
import ScrollToTop from "core/buttons/scroll-buttons/ScrollToTop";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import {
  FILTERS_FOR_NAMES,
  QUERY_STATUS,
  STATUS_MISSION,
} from "constants/constants";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { getFormattedDate } from "utils/functions";
import { getStatusLabel } from "utils/workerHelper";
import { isAfterCalendarDay } from "utils/missionHelper";
import _ from "lodash";
import InfoContext from "pages/InfoContext";
import { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { getEntityName } from "utils/helper";
import { MISSION_ROLES } from "access-roles/roles";
import { canUserPerformAction } from "access-roles/general-functions";

const CLOSED_MISSION_STATUSES = [STATUS_MISSION.FDM, STATUS_MISSION.ZERO];

const MissionsByFilters = ({ currentPage, userRole }) => {
  const intl = useIntl();
  const creationTooltip = intl.formatMessage({
    id: "mission_creation",
    defaultMessage: "Création de la mission",
  });
  const endEntryTooltip = intl.formatMessage({
    id: "end_assignment_date",
    defaultMessage: "Date de saisie de fin de mission",
  });
  const {
    missionsList,
    lang,
    userScope,
    getMoreMissions,
    loadMissionStatus,
    canEdit,
    fetchNamesForMissions,
    namesForMissions,
    workersType,
    linkToMissionBtnHandler,
  } = useContext(InfoContext);
  const { content, page, total_pages, total_items } = missionsList;

  useEffect(() => {
    if (missionsList?.content?.length) {
      const ids = missionsList.content
        .flatMap((elm) => [elm.client_id, elm.manager_id, elm.worker_id])
        .filter(Boolean);

      fetchNamesForMissions({
        id: ids,
        types: [FILTERS_FOR_NAMES.PERSON, FILTERS_FOR_NAMES.CLIENT],
        include_deleted: true,
      });
    }
  }, [missionsList]);

  const getEntitySlug = (id) => {
    const entity = namesForMissions?.find((item) => item.id === id);
    return entity?.slug;
  };

  const renderDateCell = ({ date, secondaryDate, tooltip, isAlert }) => (
    <div className="date_cell">
      <p>{getFormattedDate(date, lang)}</p>
      {secondaryDate && (
        <p
          className={`date_cell__secondary${
            isAlert ? " date_cell__secondary--alert" : ""
          }`}
          title={tooltip}
        >
          {`(${getFormattedDate(secondaryDate, lang)})`}
        </p>
      )}
    </div>
  );

  const renderClientLink = (clientId) => {
    const clientEntity = namesForMissions?.find((item) => item.id === clientId);
    return clientEntity?.deleted ? (
      <span>{clientEntity?.full_name}</span>
    ) : (
      <Link to={`/${userScope}/client/${clientId && getEntitySlug(clientId)}`}>
        {clientEntity?.full_name}
      </Link>
    );
  };

  const getFormattedMission = (list) => {
    return list?.map((elm) => {
      const worker_type = workersType?.find(
        (e) => e?.worker_id === elm?.worker_id
      )?.worker_type;
      return {
        name: elm?.name,
        client_id: renderClientLink(elm?.client_id),
        consultant: (
          <Link
            to={`/${userScope}/${
              worker_type === "cslt" ? "consultant" : "subcontractor"
            }/${elm?.worker_id && getEntitySlug(elm?.worker_id)}`}
          >
            {`${getEntityName(elm?.worker_id, namesForMissions)} ${
              worker_type === "str" ? "(STR)" : ""
            }`}
          </Link>
        ),
        manager_id: (
          <Link
            to={`/${userScope}/staff/${
              elm?.manager_id && getEntitySlug(elm?.manager_id)
            }`}
          >
            {getEntityName(elm?.manager_id, namesForMissions)}
          </Link>
        ),
        start_date: renderDateCell({
          date: elm?.start_date,
          secondaryDate: elm?.created_at,
          tooltip: creationTooltip,
          isAlert: isAfterCalendarDay({
            date: elm?.created_at,
            referenceDate: elm?.start_date,
          }),
        }),
        end_date: renderDateCell({
          date: elm?.end_date,
          secondaryDate: CLOSED_MISSION_STATUSES.includes(elm?.status)
            ? elm?.closed_at
            : null,
          tooltip: endEntryTooltip,
          isAlert: isAfterCalendarDay({
            date: elm?.closed_at,
            referenceDate: elm?.end_date,
          }),
        }),
        status: (
          <div className="status_column">
            {getStatusLabel(elm.status)}
            <div className="links">
              {canUserPerformAction({
                userRoles: userRole,
                requiredRoles: MISSION_ROLES.ROLE_GET_MISSION,
              }) && (
                <Link
                  to={`/${userScope}/missions/${elm?.slug}`}
                  className="div-link button-style"
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey || e.button === 1) return;
                    e.preventDefault();
                    linkToMissionBtnHandler({
                      missionSlug: elm?.slug,
                    });
                  }}
                >
                  <FormattedMessage
                    id="view_sheet"
                    defaultMessage={"Voir la fiche"}
                  />
                </Link>
              )}
              {canEdit && (
                <button
                  style={{ color: "inherit", padding: 0 }}
                  className="div-link button-style"
                  onClick={() => {
                    linkToMissionBtnHandler({
                      isEditBtn: true,
                      missionSlug: elm?.slug,
                    });
                  }}
                >
                  <FormattedMessage
                    id="edit_sheet"
                    defaultMessage={"Editer la fiche"}
                  />
                </button>
              )}
            </div>
          </div>
        ),
      };
    });
  };

  const header = [
    {
      label: {
        key: "assignment-title",
        value: (
          <FormattedMessage
            id="mission_title"
            defaultMessage={"Intitulé de la mission"}
          />
        ),
      },
      icon: "missions",
      minWidth: "15%",
      id: "mission_title",
    },

    {
      label: {
        key: "client",
        value: <FormattedMessage id="client" defaultMessage={"Client"} />,
      },
      icon: "user",
      minWidth: "22.3%",
      id: "client",
    },
    {
      label: {
        key: "consultant",
        value: (
          <FormattedMessage
            id="consultant_title"
            defaultMessage={"Consultant/Sous-traitant"}
          />
        ),
      },
      icon: "user",
      minWidth: "22.3%",
      id: "consultant",
    },
    {
      label: {
        key: `consultant_worker`,
        value: (
          <FormattedMessage id={`consultant_worker`} defaultMessage={"User"} />
        ),
      },
      icon: "user",
      minWidth: "13.85%",
      id: "worker",
    },
    {
      label: {
        key: "start",
        value: <FormattedMessage id="start" defaultMessage={"Début"} />,
      },
      icon: "calendar",
      minWidth: "7.9%",
      id: "start",
    },
    {
      label: {
        key: "end",
        value: <FormattedMessage id="end" defaultMessage={"Fin"} />,
      },
      icon: "calendar",
      minWidth: "7.9%",
      id: "end",
    },
    {
      label: {
        key: "status",
        value: <FormattedMessage id="status" defaultMessage={"Statut"} />,
      },
      icon: "loading",
      minWidth: "14.6%",
      id: "status",
    },
  ];

  const loadMissions = () => {
    if (
      loadMissionStatus !== QUERY_STATUS.LOADING &&
      content?.length < total_items
    ) {
      return getMoreMissions();
    }
  };

  return (
    <div className="section">
      <ScrollToTop />
      <div className="display-flex">
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <>
              <FormattedMessage id="results" defaultMessage="Résultats" />
              <span>: {total_items}</span>{" "}
            </>
          }
        />
      </div>
      <div>
        <InfiniteScroll
          pageStart={1}
          loadMore={loadMissions}
          hasMore={currentPage < total_pages}
          loader={<Loader key={page} id={page} />}
          initialLoad={false}
          useWindow={true}
        >
          <Table
            id={`missions-by-filters-${currentPage}`}
            header={header}
            content={!_.isNil(content) ? getFormattedMission(content) : []}
            tableWidth={"100%"}
            userScope={userScope}
          />
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default MissionsByFilters;

MissionsByFilters.propTypes = {
  currentPage: PropTypes.number,
  userRole: PropTypes.string,
};
